/**
 * Look up EMS display data by Safety ID code (Supabase).
 * Used by EMS Access so migrated safety IDs (e.g. KAREN, DAN007) show in the UI.
 *
 * Flow: safety_ids.safety_id → assignee (safety_id_assignments) or owner (safety_ids.user_id)
 *       → users + ems_profiles + emergency_contacts + subscription check.
 *
 * Requires RLS policies allowing anon read on: safety_ids, safety_id_assignments,
 * users, ems_profiles, emergency_contacts, subscriptions (relevant columns).
 */

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} code - Safety ID code (e.g. "KAREN", "DAN007")
 * @returns {Promise<{ user: object, ems_profile: object | null, emergency_contacts: array, hasActiveSubscription: boolean } | null>}
 */
export async function lookupSafetyIdByCode(supabase, code) {
  if (!supabase || !(code || '').trim()) return null;
  const upper = (code || '').trim().toUpperCase();
  if (!upper) return null;

  // 1) Find safety_ids row by code (stored uppercase)
  const { data: safetyRow, error: safetyErr } = await supabase
    .from('safety_ids')
    .select('id, user_id')
    .eq('safety_id', upper)
    .eq('is_active', true)
    .maybeSingle();

  if (safetyErr || !safetyRow) return null;

  // 2) Prefer assignee for EMS display; fall back to owner
  const { data: assignment } = await supabase
    .from('safety_id_assignments')
    .select('assigned_to_user_id')
    .eq('safety_id_id', safetyRow.id)
    .eq('is_active', true)
    .maybeSingle();

  const displayUserId = assignment?.assigned_to_user_id || safetyRow.user_id;
  if (!displayUserId) return null;

  // 3) Load user, ems_profile, emergency_contacts, user_profile, subscriptions in parallel
  const [userRes, emsRes, contactsRes, profileRes, subRes] = await Promise.all([
    supabase.from('users').select('id, email, first_name, last_name, phone_number').eq('id', displayUserId).maybeSingle(),
    supabase.from('ems_profiles').select('*').eq('user_id', displayUserId).maybeSingle(),
    supabase.from('emergency_contacts').select('*').eq('user_id', displayUserId),
    supabase.from('user_profiles').select('profile_picture_path, profile_picture_bucket').eq('user_id', displayUserId).maybeSingle(),
    supabase.from('subscriptions').select('id').eq('user_id', displayUserId).eq('status', 'active').limit(1),
  ]);

  if (userRes.error || !userRes.data) return null;
  const hasActiveSubscription = !subRes.error && Array.isArray(subRes.data) && subRes.data.length > 0;

  return {
    user: userRes.data,
    ems_profile: emsRes.data || null,
    emergency_contacts: Array.isArray(contactsRes.data) ? contactsRes.data : [],
    user_profile: profileRes.data || null,
    hasActiveSubscription,
  };
}

/**
 * Build profile image URL from user_profiles (Supabase Storage public URL).
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ profile_picture_path?: string | null, profile_picture_bucket?: string | null } | null} userProfile
 * @returns {string} URL or '' if missing
 */
function buildProfileImageUrl(supabase, userProfile) {
  if (!supabase || !userProfile?.profile_picture_path || !userProfile?.profile_picture_bucket) return '';
  try {
    const { data } = supabase.storage
      .from(userProfile.profile_picture_bucket)
      .getPublicUrl(userProfile.profile_picture_path);
    return data?.publicUrl || '';
  } catch {
    return '';
  }
}

/**
 * Build the riderInfo shape expected by EMSAccess.jsx (camelCase) from Supabase rows.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase - Used to build profile image URL
 * @param {object} payload - { user, ems_profile, emergency_contacts, user_profile, hasActiveSubscription }
 * @param {function} calculateAge - (dob) => number
 * @returns {object | null} riderInfo or null if no subscription
 */
export function buildRiderInfoFromSupabase(supabase, payload, calculateAge) {
  if (!payload?.user || !payload.hasActiveSubscription) return null;
  const u = payload.user;
  const ems = payload.ems_profile || {};
  const contacts = payload.emergency_contacts || [];
  const userProfile = payload.user_profile || null;

  // Visibility flags: only explicit `false` hides. Missing/null/true → show
  // (legacy records may have null columns; backend defaults to false on save but we don't
  // want to retroactively hide data that pre-dates the column).
  const showDobToEMS = ems.show_to_ems_dob !== false;
  const showGenderToEMS = ems.show_to_ems_gender !== false;
  const showHeightToEMS = ems.show_to_ems_height !== false;
  const showWeightToEMS = ems.show_to_ems_weight !== false;
  const showNotesToEMSToEMS = ems.show_to_ems_notes !== false;
  const showClassToEMS = ems.show_to_ems_class !== false;

  const rawDob = ems.dob ? (typeof ems.dob === 'string' ? ems.dob : ems.dob?.toISOString?.()?.slice(0, 10)) : null;
  const dob = showDobToEMS ? (rawDob || '') : '';
  const emergencyContacts = contacts
    .filter((c) => c.show_to_ems !== false)
    .map((c) => ({
      firstName: c.first_name,
      lastName: c.last_name,
      relationship: c.relationship || '',
      phone: c.phone_number,
      showToEMS: true,
    }));

  const showAdditionalButton =
    (ems.notes_to_ems && showNotesToEMSToEMS) ||
    emergencyContacts.length > 0;

  const imageUrl = buildProfileImageUrl(supabase, userProfile);

  return {
    firstName: u.first_name || '',
    lastName: u.last_name || '',
    fullName: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || 'Unknown',
    dob,
    age: showDobToEMS && rawDob ? calculateAge(rawDob) : null,
    class: showClassToEMS ? (ems.class || '') : '',
    imageUrl,
    gender: showGenderToEMS ? (ems.gender || '') : '',
    heightFeet: showHeightToEMS ? (ems.height_feet ?? '') : '',
    heightInches: showHeightToEMS ? (ems.height_inches ?? '') : '',
    weight: showWeightToEMS ? (ems.weight ?? '') : '',
    notesToEMS: showNotesToEMSToEMS ? (ems.notes_to_ems || '') : '',
    emergencyContacts,
    showAdditionalButton,
    showDobToEMS,
    showGenderToEMS,
    showHeightToEMS,
    showWeightToEMS,
    showNotesToEMSToEMS,
    showClassToEMS,
  };
}
